import { track } from '@ramp106/omrjs-tracking-reviews'
import { storeToRefs } from 'pinia'
import { computed, ref, watch, type ComputedRef, type Ref } from 'vue'
import { useI18n } from 'vue-i18n'

import { NotificationCenterTab, tabToQueryArgs } from '@/components/NotificationCenter/notificationCenterTabFromQuery'
import { FEATURE_FLAG } from '@/composables/featureFlagCatalog'
import { useFeatureFlag } from '@/composables/useFeatureFlag'
import {
  useArchiveMutation,
  useMarkAllAsReadMutation,
  useMarkAsReadMutation,
  useMarkAsUnreadMutation,
  useNotificationsQuery,
  useUnarchiveMutation,
  type NotificationsQuery,
} from '@/gql/platform-api'
import { showNotification } from '@/helpers/notificationHelper'
import { $can } from '@/services/roles'
import { useNotificationCenterStore } from '@/stores'

type NotificationNode = NotificationsQuery['public']['notifications']['nodes'][number]
type NotificationsPageInfo = NotificationsQuery['public']['notifications']['pageInfo']

const PER_PAGE = 20

const trackAction = (ctaId: string) =>
  track('cta_click', { surface: 'nav', category: 'engagement', cta_position: 'notification_center', cta_id: ctaId })

export const useNotificationCenterPage = (
  tab: Ref<NotificationCenterTab> | ComputedRef<NotificationCenterTab>,
  categoryPrefix: Ref<string | null> | ComputedRef<string | null>,
) => {
  const store = useNotificationCenterStore()
  const { unreadCount } = storeToRefs(store)
  const { t } = useI18n()

  const notifications = ref<NotificationNode[]>([])
  const availableCategories = ref<string[]>([])
  const pageInfo = ref<NotificationsPageInfo>({ hasNextPage: false, endCursor: null })
  const markingAll = ref(false)
  const loadingMore = ref(false)
  let paginated = false

  const filterKey = computed(() => `${tab.value}|${categoryPrefix.value ?? ''}`)
  const queryArgs = () => ({ ...tabToQueryArgs(tab.value), categoryPrefix: categoryPrefix.value ?? undefined })

  const { enabled } = useFeatureFlag(FEATURE_FLAG.NOTIFICATIONS.CENTER_RELEASE)
  const { mutate: markAsReadMutate } = useMarkAsReadMutation({ clientId: 'platformApi' })
  const { mutate: markAsUnreadMutate } = useMarkAsUnreadMutation({ clientId: 'platformApi' })
  const { mutate: markAllAsReadMutate } = useMarkAllAsReadMutation({ clientId: 'platformApi' })
  const { mutate: archiveMutate } = useArchiveMutation({ clientId: 'platformApi' })
  const { mutate: unarchiveMutate } = useUnarchiveMutation({ clientId: 'platformApi' })

  const { result, fetchMore, refetch, loading } = useNotificationsQuery(
    () => ({ first: PER_PAGE, ...queryArgs() }),
    () => ({
      clientId: 'platformApi',
      fetchPolicy: 'network-only',
      enabled: enabled.value && $can('manageReviews', { for: 'User' }),
    }),
  )

  const reloadFirstPage = async () => {
    paginated = false
    store.requestUnreadCountSync()
    await refetch()
  }

  watch(filterKey, () => {
    paginated = false
    store.requestUnreadCountSync()
    notifications.value = []
    pageInfo.value = { hasNextPage: false, endCursor: null }
  })

  watch(
    result,
    (value) => {
      if (!value?.public) return
      store.syncUnreadCountFromQuery(value.public.notifications.unreadCount)
      if (paginated) {
        const emitted = new Map(value.public.notifications.nodes.map((node) => [node.id, node]))
        notifications.value = notifications.value.map((item) => emitted.get(item.id) ?? item)
        return
      }
      availableCategories.value = value.public.notifications.availableCategories
      notifications.value = value.public.notifications.nodes
      pageInfo.value = value.public.notifications.pageInfo
    },
    { immediate: true },
  )

  const loadMore = async () => {
    if (loadingMore.value) return
    if (!pageInfo.value.hasNextPage || !pageInfo.value.endCursor) return
    const keyAtStart = filterKey.value
    loadingMore.value = true
    try {
      const next = await fetchMore({
        variables: { first: PER_PAGE, after: pageInfo.value.endCursor, ...queryArgs() },
      })
      if (filterKey.value !== keyAtStart) return
      if (next?.data?.public?.notifications) {
        paginated = true
        notifications.value = [...notifications.value, ...next.data.public.notifications.nodes]
        pageInfo.value = next.data.public.notifications.pageInfo
      }
    } catch {
      showNotification(t('Dashboard.notifications.actionError'), 'error', 10000)
    } finally {
      loadingMore.value = false
    }
  }

  const setReadAt = (id: string, readAt: string | null) => {
    notifications.value = notifications.value.map((item) => (item.id === id ? { ...item, readAt } : item))
  }

  const applyMarkRead = async (target: NotificationNode) => {
    const tabAtStart = tab.value
    await markAsReadMutate({ ids: [target.id] })
    if (tab.value === tabAtStart) {
      if (tabAtStart === NotificationCenterTab.UNREAD) {
        notifications.value = notifications.value.filter((item) => item.id !== target.id)
      } else {
        setReadAt(target.id, new Date().toISOString())
      }
    }
    if (!target.archivedAt) store.setUnreadCount(unreadCount.value - 1)
  }

  const toggleRead = async (id: string) => {
    const target = notifications.value.find((item) => item.id === id)
    if (!target) return
    const markRead = !target.readAt
    trackAction(markRead ? 'notification_mark_read' : 'notification_mark_unread')
    try {
      if (markRead) {
        await applyMarkRead(target)
      } else {
        const tabAtStart = tab.value
        await markAsUnreadMutate({ ids: [id] })
        if (tab.value === tabAtStart) setReadAt(id, null)
        if (!target.archivedAt) store.setUnreadCount(unreadCount.value + 1)
      }
    } catch {
      showNotification(t('Dashboard.notifications.actionError'), 'error', 10000)
    }
  }

  const undoArchive = async (ids: string[]) => {
    try {
      await unarchiveMutate({ ids })
      await reloadFirstPage()
    } catch {
      showNotification(t('Dashboard.notifications.actionError'), 'error', 10000)
    }
  }

  const archiveOne = async (id: string) => {
    const target = notifications.value.find((item) => item.id === id)
    if (!target) return
    const tabAtStart = tab.value
    trackAction('notification_archive')
    try {
      await archiveMutate({ ids: [id] })
    } catch {
      showNotification(t('Dashboard.notifications.actionError'), 'error', 10000)
      return
    }
    if (tab.value === tabAtStart) notifications.value = notifications.value.filter((item) => item.id !== id)
    if (!target.readAt) store.setUnreadCount(unreadCount.value - 1)
    showNotification(t('NotificationCenter.archivedMessage'), 'success', 10000, true, [
      { title: t('Dashboard.notifications.undo'), handler: () => undoArchive([id]) },
    ])
  }

  const restoreOne = async (id: string) => {
    const target = notifications.value.find((item) => item.id === id)
    if (!target) return
    const tabAtStart = tab.value
    trackAction('notification_restore')
    try {
      await unarchiveMutate({ ids: [id] })
      if (tab.value === tabAtStart) notifications.value = notifications.value.filter((item) => item.id !== id)
      if (!target.readAt) store.setUnreadCount(unreadCount.value + 1)
    } catch {
      showNotification(t('Dashboard.notifications.actionError'), 'error', 10000)
    }
  }

  const markAllAsRead = async () => {
    if (markingAll.value) return
    markingAll.value = true
    trackAction('notification_mark_all_read')
    try {
      const response = await markAllAsReadMutate()
      const nextUnreadCount = response?.data?.public.notifications.markAllAsRead.unreadCount
      if (nextUnreadCount !== undefined) store.setUnreadCount(nextUnreadCount)
      await reloadFirstPage()
    } catch {
      showNotification(t('Dashboard.notifications.actionError'), 'error', 10000)
    } finally {
      markingAll.value = false
    }
  }

  const handleCtaClick = async (id: string) => {
    const target = notifications.value.find((item) => item.id === id)
    if (target) track('notification_click', { surface: 'nav', notification_category: target.category, notification_id: target.uuid })
    if (!target || target.readAt) return
    try {
      await applyMarkRead(target)
    } catch {
      showNotification(t('Dashboard.notifications.actionError'), 'error', 10000)
    }
  }

  return {
    notifications,
    availableCategories,
    pageInfo,
    loading,
    loadingMore,
    markingAll,
    unreadCount,
    loadMore,
    toggleRead,
    archiveOne,
    restoreOne,
    markAllAsRead,
    handleCtaClick,
  }
}
